/**
 * Actions
 *
 * Keep in mind that actions are complex, and this isn't the only component that renders them. Make sure you check how
 * changes to methods that return TemplateResults will affect actions inside loops and filters.
 */

// Import dependencies
import './resend-button';

import { AutomatorLogItem } from '@pages/new-logs/components/log-item/_base';
import { unsafeCSS, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { isEmpty, htmlEntities } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logItemAction.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-item-action' )
export class AutomatorLogAction extends AutomatorLogItem {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute: false } ) accessor resultMessage = '';

	@property( { type: Boolean, attribute: 'can-rerun' } ) accessor canReRun = false;

	@property( { type: String, attribute: 'item-log-id' } ) accessor itemLogID;

	/**
	 * Returns the HTML of the section that shows the number of runs
	 * This will return HTML only if there were more than 1 runs.
	 *
	 * @return {TemplateResult} The number of runs
	 */
	get _templateRuns() {
		// Check if there if there is more than one, otherwise don't return anything
		if ( this.runs.length <= 1 ) {
			return html``;
		}

		return html`
			<div class="item__detail">
				<uo-icon class="item__detail-icon" id="hashtag"></uo-icon>

				${ sprintf( strings.tries, this.runs.length ) }
			</div>
		`;
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The sidebar content
	 */
	get sidebarContent() {
		return {
			title: strings.sidebarTitle,
			summary: this.sidebarSummary,
			runs: this.sidebarRuns
		}
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} The sidebar summary
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// Add status
		items.push( {
			label: strings.summary.status,
			value: html`
				<uap-log-status status-id="${ this.statusID }"></uap-log-status>
			`
		} );

		// Add start and completion date
		// If it's a range, add "Start date" and "End date". If there is no end date, then just add "Date"
		const dateIsRange = this.startDate !== this.endDate;

		// Check if there is a start date (it won't for skipped actions)
		if ( ! isEmpty ( this.startDate ) ) {
			items.push( {
				label: dateIsRange ? strings.summary.startDate : strings.summary.date,
				value: this.startDate
			} );

			if ( dateIsRange ) {
				// Add "End date"
				items.push( {
					label: strings.summary.endDate,
					value: this.endDate
				} );
			}
		}

		// Add the runs, but only if there is more than one
		if ( this.runs.length > 1 ) {
			items.push( {
				label: strings.summary.runs,
				value: this.runs.length
			} );
		}

		// Check if there is an error message
		if ( ! isEmpty( this.resultMessage ) ) {
			items.push( {
				label: strings.summary.message,
				value: htmlEntities.decode( this.resultMessage )
			} );
		}

		// Add the tasks that we can perform in the sidebar, such as re-run
		const actionTasks = [];

		// Check if the action can re-run
		if ( this.canReRun ) {
			actionTasks.push(
				html`
					<uap-log-item-action-resend-button
						item-id="${ this.id }"
						item-log-id="${ this.itemLogID }"

						?import-mode=${ this.isImportMode }
					>
					</uap-log-item-action-resend-button>
				`
			);
		}

		if ( ! isEmpty( actionTasks ) ) {
			items.push( {
				label: strings.summary.events,
				value: repeat(
					actionTasks,
					( task, index ) => index,
					task => task
				)
			} );
		}

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the action runs
	 *
	 * @return {Array} The array of runs
	 */
	get sidebarRuns() {
		if ( this.runs.length === 0 ) {
			return [];
		}

		const runs = [];

		// Get the number of runs
		const numberOfRuns = this.runs.length;

		this.runs.forEach( ( run, index ) => {
			// Create a summary of each run
			const runSummary = [];

			// Add the run status
			runSummary.push( {
				label: strings.summary.status,
				value: html`
					<uap-log-status status-id="${ run.status_id }"></uap-log-status>
				`
			} );

			// Add the date
			runSummary.push( {
				label: strings.summary.date,
				value: run.date
			} );

			// Check if there is an error message
			if ( ! isEmpty( run.result_message ) ) {
				runSummary.push( {
					label: strings.summary.message,
					value: htmlEntities.decode( run.result_message )
				} );
			}

			runs.push( {
				title: html`
					<uap-log-status status-id="${ run.status_id }" hide-label></uap-log-status>
					${ sprintf( strings.tryNumber, `${ ( index + 1 ) }/${ numberOfRuns }` ) }
				`,
				summary: runSummary,
				properties: [ ...this.fieldsForSidebar, ...run.properties ]
			} );
		} );

		return runs;
	}

	/**
	 * The message shown when the action data is missing
	 *
	 * @return {String} The message
	 */
	get dataMissingErrorMessage() {
		return strings.missingItem;
	}
}
